import React, { useEffect, useRef, useState } from 'react';
import { useFocusTrap } from '../../utils/focusTrap';
import { CopyButton } from '../shared';
import type { LocalVersionTokens } from '../../hooks/useLocalVersions';
import styles from './LocalVersionsSection.module.css';

interface LocalVersionTokensModalProps {
  isOpen: boolean;
  onClose: () => void;
  versionId: string;
  tokens: LocalVersionTokens | null;
  isLoading: boolean;
  error: string | null;
  onCycleTokens: () => Promise<void>;
  isCycling: boolean;
}

export const LocalVersionTokensModal: React.FC<LocalVersionTokensModalProps> = ({
  isOpen,
  onClose,
  versionId,
  tokens,
  isLoading,
  error,
  onCycleTokens,
  isCycling,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [showCycleWarning, setShowCycleWarning] = useState(false);

  useFocusTrap(modalRef, isOpen);

  useEffect(() => {
    if (isOpen) {
      setShowCycleWarning(false);
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          onClose();
        }
      };
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, onClose]);

  const formatExpiry = (expiresAt: string) => {
    const date = new Date(expiresAt);
    const now = new Date();
    const diff = date.getTime() - now.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 0) {
      return `${hours}h ${minutes}m remaining`;
    }
    if (minutes > 0) {
      return `${minutes}m remaining`;
    }
    return 'Expired';
  };

  const handleCycleClick = () => {
    if (showCycleWarning) {
      onCycleTokens();
      setShowCycleWarning(false);
    } else {
      setShowCycleWarning(true);
    }
  };

  const downloadTokensJson = () => {
    if (!tokens) return;

    const data = {
      localVersionId: tokens.localVersionId,
      browserAdmissionToken: tokens.browserAdmissionToken,
      workerAdmissionToken: tokens.workerAdmissionToken,
      expiresAt: tokens.expiresAt,
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `local-version-tokens-${versionId}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  return (
    <div
      className={styles.modalOverlay}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="tokens-modal-title"
    >
      <div ref={modalRef} className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h2 id="tokens-modal-title" className={styles.modalTitle}>
          Tokens: {versionId}
        </h2>

        {isLoading && (
          <div className={styles.emptyState}>Loading tokens...</div>
        )}

        {error && (
          <div className={styles.errorText}>{error}</div>
        )}

        {tokens && !isLoading && (
          <>
            <div className={styles.tokenSection}>
              <label className={styles.tokenLabel}>Browser Admission Token</label>
              <div className={styles.tokenBox}>
                <span className={styles.tokenValue}>{tokens.browserAdmissionToken}</span>
                <CopyButton value={tokens.browserAdmissionToken} aria-label="Copy browser token" />
              </div>
            </div>

            <div className={styles.tokenSection}>
              <label className={styles.tokenLabel}>Local Agent Admission Token</label>
              <div className={styles.tokenBox}>
                <span className={styles.tokenValue}>{tokens.workerAdmissionToken}</span>
                <CopyButton value={tokens.workerAdmissionToken} aria-label="Copy worker token" />
              </div>
            </div>

            <div className={styles.tokenMeta}>
              <span className={styles.expiryInfo}>
                Expires: {formatExpiry(tokens.expiresAt)}
              </span>
              <span className={styles.versionIdInfo}>
                ID: {tokens.localVersionId.substring(0, 8)}...
              </span>
            </div>

            {showCycleWarning && (
              <div className={styles.warningMessage}>
                Cycling tokens will invalidate the current tokens. Any active connections using these tokens will be disconnected. Are you sure?
              </div>
            )}

            <div className={styles.tokenActions}>
              <button
                type="button"
                onClick={downloadTokensJson}
                className={`${styles.buttonSmall} ${styles.buttonSecondary}`}
              >
                Download JSON
              </button>
              <button
                type="button"
                onClick={handleCycleClick}
                disabled={isCycling}
                className={`${styles.buttonSmall} ${showCycleWarning ? styles.buttonDelete : ''}`}
              >
                {isCycling ? 'Cycling...' : showCycleWarning ? 'Confirm Cycle' : 'Cycle Tokens'}
              </button>
            </div>
          </>
        )}

        <div className={styles.modalActions}>
          <button
            type="button"
            onClick={onClose}
            className={`${styles.buttonSmall} ${styles.buttonSecondary}`}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
